import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import JsSIP from 'jssip'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

interface SipContextType {
  ua: any
  session: any
  tokenInfo: any
  errorStatus: string | null
  setSession: (session: any) => void
  makeCall: (to: string) => void
  hangup: () => void
  answer: () => void
  isMuted: boolean
  toggleMute: () => void
  callStatus: 'idle' | 'calling' | 'ringing' | 'connected'
}

const SipContext = createContext<SipContextType | null>(null)

export function useSip() {
  const ctx = useContext(SipContext)
  if (!ctx) throw new Error('useSip must be used within SipProvider')
  return ctx
}

export function SipProvider({ children }: { children: React.ReactNode }) {
  const [ua, setUa] = useState<any>(null)
  const [session, setSession] = useState<any>(null)
  const [tokenInfo, setTokenInfo] = useState<any>(null)
  const [errorStatus, setErrorStatus] = useState<string | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [callStatus, setCallStatus] = useState<'idle' | 'calling' | 'ringing' | 'connected'>('idle')

  const uaRef = useRef<any>(null)
  const sessionRef = useRef<any>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const navigate = useNavigate()

  // 1. Fetch token and initialize UA singleton
  useEffect(() => {
    async function initSIP() {
      // 1. Pre-warm mic permission on page load
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => { 
          stream.getTracks().forEach(t => t.stop()); 
          console.log('[SIP] 🎤 Mic pre-warmed on page load') 
        })
        .catch(e => console.warn('[SIP] Mic pre-warm failed:', e))

      const { data: { session: authSession } } = await supabase.auth.getSession()
      if (!authSession) return

      const { data, error } = await supabase.functions.invoke('signalwire-token', {
        headers: { Authorization: `Bearer ${authSession.access_token}` }
      })
      
      if (error || !data) {
        setErrorStatus(error?.message || 'Dialer unavailable')
        return
      }
      setTokenInfo(data)

      // Clean up any existing instance (safety net)
      if (uaRef.current) {
        uaRef.current.stop()
      }

      try {
        const socket = new JsSIP.WebSocketInterface(data.sip_wss)
        const newUa = new JsSIP.UA({
          sockets:        [socket],
          uri:            `sip:${data.sip_username}@${data.sip_domain}`,
          password:       data.sip_password,
          register:       true,
        })
        
        newUa.on('registered', () => {
          console.log('[SIP] ✅ Registered successfully — UA is live')
        })

        newUa.on('registrationFailed', (e: any) => {
          setErrorStatus('SIP Registration failed')
          console.error('[SIP] ❌ Registration failed:', e.cause || e)
        })

        newUa.on('newRTCSession', (e: any) => {
          const rtcSession = e.session
          console.log('[SIP] 📞 newRTCSession fired — direction:', rtcSession.direction)

          if (rtcSession.direction === 'incoming') {
            console.log('[SIP] 📲 Incoming call from:', rtcSession.remote_identity?.uri?.toString())
            console.log('[SIP] Session state:', rtcSession.status)

            // Mute agent mic IMMEDIATELY — consent must play before they can speak
            rtcSession.mute()
            setIsMuted(true)
            setCallStatus('ringing')
            toast('Incoming Call! Routing to Dialer...', { icon: '📞', duration: 4000 })
            setSession(rtcSession)
            sessionRef.current = rtcSession
            navigate('/calls')
            bindSessionEvents(rtcSession)
          } else if (rtcSession.direction === 'outgoing') {
            // Outbound: mute until consent plays on confirmed
            rtcSession.mute()
            setIsMuted(true)
            setCallStatus('calling')
            setSession(rtcSession)
            sessionRef.current = rtcSession
            bindSessionEvents(rtcSession)
          }
        })

        console.log('[SIP] 🚀 UA starting — uri:', `sip:${data.sip_username}@${data.sip_domain}`)
        newUa.start()
        uaRef.current = newUa
        setUa(newUa)


      } catch (err: any) {
        console.error('SIP Init error', err)
        setErrorStatus('SIP Init error')
      }
    }
    
    initSIP()

    return () => {
      if (sessionRef.current?.terminate) sessionRef.current.terminate()
      if (uaRef.current) uaRef.current.stop()
    }
  }, [navigate])

  const bindSessionEvents = (rtcSession: any) => {
    rtcSession.on('confirmed', async () => {
      setCallStatus('connected')
      console.log('[SIP] Call confirmed — injecting consent into outbound stream')

      try {
        const conn: RTCPeerConnection = rtcSession.connection

        // Bug 2 fix: wait for ICE to complete before calling replaceTrack.
        // On inbound calls, 'confirmed' fires before connectionState === 'connected',
        // causing 'InvalidStateError: peer connection is closed'.
        if (conn.connectionState !== 'connected') {
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('ICE connection timeout')), 10000)
            conn.addEventListener('connectionstatechange', () => {
              if (conn.connectionState === 'connected') {
                clearTimeout(timeout)
                resolve()
              } else if (conn.connectionState === 'failed' || conn.connectionState === 'closed') {
                clearTimeout(timeout)
                reject(new Error(`Connection ${conn.connectionState}`))
              }
            })
          })
        }

        const sender = conn.getSenders().find((s: RTCRtpSender) => s.track?.kind === 'audio')

        if (!sender) throw new Error('No audio sender found on RTCPeerConnection')

        // Route consent.wav THROUGH the outbound RTP track — customer hears it
        const audioCtx = new AudioContext()
        const consentAudio = new Audio('/consent.wav')
        consentAudio.crossOrigin = 'anonymous'
        const source = audioCtx.createMediaElementSource(consentAudio)
        const dest   = audioCtx.createMediaStreamDestination()
        source.connect(dest)
        source.connect(audioCtx.destination) // agent also hears it locally

        // Replace the mic track with the consent audio stream
        await sender.replaceTrack(dest.stream.getTracks()[0])
        await consentAudio.play()

        await new Promise<void>((resolve, reject) => {
          consentAudio.onended = () => resolve()
          consentAudio.onerror = (err) => reject(err)
        })

        // Restore mic track after consent finishes
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        await sender.replaceTrack(micStream.getTracks()[0])
        await audioCtx.close()

        rtcSession.unmute({ audio: true })
        setIsMuted(false)
        console.log('[SIP] Consent complete — mic restored, agent unmuted')

      } catch (err) {
        console.warn('[SIP] Consent injection failed, unmuting as fallback:', err)
        rtcSession.unmute()
        setIsMuted(false)
      }
    })
    
    rtcSession.on('ended', () => handlePostCallCleanup())
    rtcSession.on('failed', () => handlePostCallCleanup())

    const wireConnection = (conn: RTCPeerConnection) => {
      // Attach any tracks already present
      conn.getReceivers().forEach((receiver: any) => {
        if (receiver.track?.kind === 'audio') {
          attachStream(new MediaStream([receiver.track]))
        }
      })
      // Attach tracks added later
      conn.addEventListener('track', (e: any) => {
        if (e.streams?.[0]) attachStream(e.streams[0])
        else if (e.track)   attachStream(new MediaStream([e.track]))
      })
    }

    const attachStream = (stream: MediaStream) => {
      if (!audioRef.current) {
        audioRef.current = new Audio()
      }
      audioRef.current.srcObject = stream
      audioRef.current.play().catch(console.error)
    }

    if (rtcSession.connection) {
      wireConnection(rtcSession.connection)
    } else {
      rtcSession.on('peerconnection', (data: any) => wireConnection(data.peerconnection))
    }
  }

  const makeCall = (to: string) => {
    if (!uaRef.current) return
    setErrorStatus(null)
    setCallStatus('calling')

    const sipTo = to.trim().startsWith('+') ? to.trim() : `+1${to.trim().replace(/\D/g, '')}`
    
    // newRTCSession fires synchronously inside .call() for outgoing sessions.
    // The global listener on uaRef handles outgoing direction — no duplicate listener needed here.
    uaRef.current.call(
      `sip:${sipTo}@getcallro-9e9b49ba1cb3.sip.signalwire.com`,
      {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] },
        sessionTimersExpires: 120,
        extraHeaders: [],
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false,
        },
      }
    )
  }

  const answer = () => {
    if (sessionRef.current && sessionRef.current.direction === 'incoming') {
      // Start muted to comply with consent requirement
      sessionRef.current.mute()
      setIsMuted(true)
      sessionRef.current.answer({
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }
      })
    }
  }

  const toggleMute = () => {
    if (sessionRef.current) {
      if (isMuted) sessionRef.current.unmute()
      else sessionRef.current.mute()
      setIsMuted(!isMuted)
    }
  }

  const hangup = () => {
    if (sessionRef.current) {
      sessionRef.current.terminate()
    }
    handlePostCallCleanup()
  }

  const handlePostCallCleanup = () => {
    setCallStatus('idle')
    setSession(null)
    sessionRef.current = null
    setIsMuted(false)
    if (audioRef.current) {
      audioRef.current.srcObject = null
    }
  }

  return (
    <SipContext.Provider value={{ ua, session, tokenInfo, errorStatus, setSession, makeCall, hangup, answer, isMuted, toggleMute, callStatus }}>
      {children}
    </SipContext.Provider>
  )
}
